const normalizeDateOnly = (dateValue) => {
  if (!dateValue) return '';
  return typeof dateValue === 'string' ? dateValue.slice(0, 10) : '';
};

const addDays = (dateValue, days) => {
  if (!dateValue) return '';
  const date = new Date(`${normalizeDateOnly(dateValue)}T00:00:00`);
  if (Number.isNaN(date.getTime())) return '';
  date.setDate(date.getDate() + days);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const getFinancialYearSortKey = (financialYear) =>
  normalizeDateOnly(financialYear?.startDate)
  || addDays(financialYear?.endDate, 1)
  || normalizeDateOnly(financialYear?.endDate)
  || '';

export const isCurrentFinancialYear = (financialYear, now = new Date()) => {
  if (!financialYear?.startDate || !financialYear?.endDate) return false;

  const current = new Date(now);
  const start = new Date(`${financialYear.startDate}T00:00:00`);
  const end = new Date(`${financialYear.endDate}T23:59:59.999`);

  return current >= start && current <= end;
};

export const isPastFinancialYear = (financialYear, now = new Date()) => {
  if (!financialYear?.endDate) return false;
  return new Date(`${financialYear.endDate}T23:59:59.999`) < new Date(now);
};

export const isFutureFinancialYear = (financialYear, now = new Date()) => {
  if (!financialYear?.startDate) return false;
  return new Date(`${financialYear.startDate}T00:00:00`) > new Date(now);
};

export const isUnlockedFinancialYear = (financialYear) => !!financialYear && !financialYear.isClosed;

export const sortFinancialYearsAsc = (financialYears = []) =>
  [...financialYears].sort((a, b) => getFinancialYearSortKey(a).localeCompare(getFinancialYearSortKey(b)));

export const getUnlockedFinancialYears = (financialYears = []) =>
  (financialYears || []).filter((financialYear) => isUnlockedFinancialYear(financialYear));

export const getCurrentFinancialYear = (financialYears, now = new Date()) =>
  (financialYears || []).find((financialYear) => isCurrentFinancialYear(financialYear, now)) || null;

export const getCurrentUnlockedFinancialYear = (financialYears, now = new Date()) =>
  (financialYears || []).find(
    (financialYear) => isCurrentFinancialYear(financialYear, now) && !financialYear.isClosed
  ) || null;

export const getPreferredPostingFinancialYear = (financialYears, now = new Date()) =>
  getCurrentUnlockedFinancialYear(financialYears, now) || getUnlockedFinancialYears(financialYears)[0] || null;

export const getDefaultSelectableFinancialYear = (financialYears, now = new Date()) =>
  getCurrentFinancialYear(financialYears, now) || (financialYears || [])[0] || null;

export const getFirstPastFinancialYear = (financialYears, now = new Date()) =>
  sortFinancialYearsAsc(financialYears).find((financialYear) => isPastFinancialYear(financialYear, now)) || null;

// Opening Balance is editable only on the first financial year (earliest startDate).
// There is no longer a separate "Opening" year.
export const canEditOpeningBalanceYear = (financialYear, financialYears = []) => {
  if (!financialYear) return false;
  const sorted = sortFinancialYearsAsc(financialYears);
  if (!sorted.length) return false;
  return String(sorted[0].id) === String(financialYear.id);
};

// Legacy compat: callers that asked for the "opening" FY now get the first FY.
export const getOpeningFinancialYear = (financialYears = []) => {
  const sorted = sortFinancialYearsAsc(financialYears);
  return sorted[0] || null;
};

// Legacy compat: previously this filtered out the phantom Opening year. Now all
// FYs are real, so just return them sorted ascending. Kept for callers still importing.
export const getVisibleFinancialYears = (financialYears = []) => sortFinancialYearsAsc(financialYears);

// Legacy compat: callers checking for the phantom Opening year always get false now.
export const isOpeningFinancialYear = () => false;
